import moment from "moment";
import { getMessageTimestamp } from "./polls/pollClient";

const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;
export const OPTIMISTIC_MESSAGE_ID_PREFIX = "optimistic:";
export const CHAT_ACTIVITY_MESSAGE_PREFIX = "[[chat_activity]]";

function getObjectIdTimestampMs(value) {
	const stringValue = String(value || "");
	if (!OBJECT_ID_REGEX.test(stringValue))
	{
		return 0;
	}

	return parseInt(stringValue.slice(0, 8), 16) * 1000;
}

export function getMessageTimestampMs(message = {}) {
	const normalizedTimestamp = getMessageTimestamp(message);
	const timestampMs = new Date(normalizedTimestamp).getTime();

	if (Number.isFinite(timestampMs) && timestampMs > 0)
	{
		return timestampMs;
	}

	return getObjectIdTimestampMs(message?._id);
}

export function getMessageDateKey(message = {}) {
	const timestampMs = getMessageTimestampMs(message);
	return timestampMs > 0 ? moment(timestampMs).format("DD-MM-YYYY") : "";
}

export function getMessageDateLabel(message = {}) {
	const timestampMs = getMessageTimestampMs(message);
	return timestampMs > 0 ? moment(timestampMs).format("DD.MM.YYYY") : "";
}

export function getMessageSenderId(message = {}) {
	return String(message?.from?._id || message?.from || "");
}

export function getChatEntityId(value) {
	return String(value?._id || value || "");
}

export function getUniqueChatEntityIds(values = []) {
	return Array.from(
		new Set(
			(Array.isArray(values) ? values : [])
				.map((value) => getChatEntityId(value))
				.filter(Boolean)
		)
	);
}

export function hasChatEntityId(values = [], targetValue) {
	const targetId = getChatEntityId(targetValue);
	if (!targetId)
	{
		return false;
	}

	return getUniqueChatEntityIds(values).includes(targetId);
}

export function getMessageRecipientIds(message = {}, fallbackRecipients = []) {
	const senderId = getMessageSenderId(message);
	const recipientIdsFromMessage = getUniqueChatEntityIds(message?.to);

	if (recipientIdsFromMessage.length > 0)
	{
		return recipientIdsFromMessage;
	}

	return getUniqueChatEntityIds(fallbackRecipients).filter(
		(participantId) => participantId !== senderId
	);
}

export function isOptimisticMessage(message = {}) {
	const messageId = getChatEntityId(message?._id);
	return (
		message?.sendStatus === "pending" ||
		(Boolean(messageId) && messageId.startsWith(OPTIMISTIC_MESSAGE_ID_PREFIX))
	);
}

export function hasPersistedMessageId(message = {}) {
	return OBJECT_ID_REGEX.test(getChatEntityId(message?._id));
}

export function isChatActivityMessageBody(body = "") {
	return typeof body === "string" && body.startsWith(CHAT_ACTIVITY_MESSAGE_PREFIX);
}

export function createOptimisticClientMessageId() {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
	{
		return `${OPTIMISTIC_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`;
	}

	return `${OPTIMISTIC_MESSAGE_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildOptimisticOutgoingMessage({
	clientMessageId,
	user = {},
	chatId = "",
	to = [],
	body = "",
	mentions = [],
	uploadInProgress = false,
	extraFields = {}
} = {}) {
	const createdAt = new Date();
	const senderId = getChatEntityId(user?._id || user);

	return {
		...extraFields,
		_id: clientMessageId,
		clientMessageId,
		from: user,
		to,
		chatId,
		body,
		mentions,
		readBy: senderId ? [senderId] : [],
		deliveredTo: [],
		created: createdAt,
		createdAt,
		sendStatus: "pending",
		uploadInProgress: Boolean(uploadInProgress)
	};
}

export function reconcileMessagesWithServerMessage(messages = [], serverMessage = {}) {
	const serverMessageId = getChatEntityId(serverMessage?._id);
	const clientMessageId = String(serverMessage?.clientMessageId || "");
	let reconciled = false;

	const nextMessages = (Array.isArray(messages) ? messages : []).map((message) => {
		const matchesServerId =
			Boolean(serverMessageId) && getChatEntityId(message?._id) === serverMessageId;
		const matchesClientId =
			Boolean(clientMessageId) &&
			(message?.clientMessageId === clientMessageId ||
				getChatEntityId(message?._id) === clientMessageId);

		if (matchesServerId || matchesClientId)
		{
			reconciled = true;
			return {
				...serverMessage,
				sendStatus: "sent"
			};
		}

		return message;
	});

	if (reconciled)
	{
		return sortMessagesChronologically(nextMessages);
	}

	if (!serverMessageId)
	{
		return sortMessagesChronologically(nextMessages);
	}

	const messageExists = nextMessages.some(
		(message) => getChatEntityId(message?._id) === serverMessageId
	);

	if (messageExists)
	{
		return sortMessagesChronologically(
			nextMessages.map((message) =>
				getChatEntityId(message?._id) === serverMessageId ? serverMessage : message
			)
		);
	}

	return sortMessagesChronologically([...nextMessages, serverMessage]);
}

export function getMessageReadReceiptState({
	message = {},
	participants = [],
	currentUserId = ""
} = {}) {
	const senderId = getMessageSenderId(message);
	const recipientIds = getMessageRecipientIds(message, participants);
	const readByIds = getUniqueChatEntityIds(message?.readBy);
	const deliveredToIds = getUniqueChatEntityIds(message?.deliveredTo);
	const normalizedCurrentUserId = getChatEntityId(currentUserId);
	const readParticipantIds = recipientIds.filter((recipientId) =>
		readByIds.includes(recipientId)
	);
	const unreadParticipantIds = recipientIds.filter(
		(recipientId) => !readByIds.includes(recipientId)
	);
	const deliveredParticipantIds = recipientIds.filter((recipientId) =>
		deliveredToIds.includes(recipientId)
	);
	const undeliveredParticipantIds = recipientIds.filter(
		(recipientId) => !deliveredToIds.includes(recipientId)
	);

	return {
		senderId,
		recipientIds,
		readByIds,
		deliveredToIds,
		readParticipantIds,
		unreadParticipantIds,
		deliveredParticipantIds,
		undeliveredParticipantIds,
		isReadByCurrentUser: normalizedCurrentUserId
			? readByIds.includes(normalizedCurrentUserId)
			: false,
		canCurrentUserRead: normalizedCurrentUserId
			? senderId !== normalizedCurrentUserId &&
			  recipientIds.includes(normalizedCurrentUserId)
			: false,
		hasAnyRecipientRead: readParticipantIds.length > 0,
		allRecipientsRead:
			recipientIds.length > 0 && unreadParticipantIds.length === 0,
		hasAnyRecipientDelivered: deliveredParticipantIds.length > 0,
		allRecipientsDelivered:
			recipientIds.length > 0 && undeliveredParticipantIds.length === 0
	};
}

export function getMessageOutboundReceiptStatus({
	message = {},
	participants = [],
	currentUserId = ""
} = {}) {
	const receiptState = getMessageReadReceiptState({
		message,
		participants,
		currentUserId
	});

	if (message?.sendStatus === "failed")
	{
		return "failed";
	}

	if (isOptimisticMessage(message) || message?.sendStatus === "pending")
	{
		return "pending";
	}

	if (receiptState.allRecipientsRead)
	{
		return "read";
	}

	if (receiptState.allRecipientsDelivered)
	{
		return "delivered";
	}

	return "sent";
}

export function areMessagesGrouped(leftMessage, rightMessage) {
	if (!leftMessage || !rightMessage) return false;

	const leftSenderId = getMessageSenderId(leftMessage);
	const rightSenderId = getMessageSenderId(rightMessage);

	if (!leftSenderId || leftSenderId !== rightSenderId)
	{
		return false;
	}

	return getMessageDateKey(leftMessage) === getMessageDateKey(rightMessage);
}

export function sortMessagesChronologically(messages = []) {
	return [...messages].sort((leftMessage, rightMessage) => {
		const timestampDifference =
			getMessageTimestampMs(leftMessage) - getMessageTimestampMs(rightMessage);

		if (timestampDifference !== 0)
		{
			return timestampDifference;
		}

		return String(leftMessage?._id || "").localeCompare(String(rightMessage?._id || ""));
	});
}

function htmlToPlainText(html = "") {
	const source = String(html || "");
	if (!source) return "";
	const withBreaks = source
		.replace(/<br\s*\/?>/gi, "\n")
		.replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n");
	const doc = new DOMParser().parseFromString(withBreaks, "text/html");
	return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

/** Plain text a "Copy" action should put on the clipboard; empty when there is nothing textual. */
export function getMessageCopyText(message = {}) {
	if (isChatActivityMessageBody(message?.body)) return "";
	const body = htmlToPlainText(message?.body);
	if (body && !message?.poll && !message?.location) return body;

	const forwarded = htmlToPlainText(message?.forward?.body || message?.forward?.previewText);
	if (forwarded) return forwarded;

	const location = message?.location;
	if (location && Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude))) {
		const label = [location.title, location.address].filter(Boolean).join(", ");
		const link = `https://maps.google.com/?q=${location.latitude},${location.longitude}`;
		return label ? `${label}\n${link}` : link;
	}

	const poll = message?.poll;
	if (poll?.question) {
		return [poll.question, ...(poll.options || []).map((option) => `• ${option?.text || ""}`)].join("\n");
	}

	return String(message?.transcription?.text || "").trim();
}

/** navigator.clipboard only exists in secure contexts; fall back for plain-HTTP LAN access. */
export async function copyTextToClipboard(text) {
	if (!text) return false;
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch (_) {
			// Fall through to the legacy path (e.g. permission denied).
		}
	}
	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.opacity = "0";
	document.body.appendChild(textarea);
	textarea.select();
	try {
		return document.execCommand("copy");
	} catch (_) {
		return false;
	} finally {
		document.body.removeChild(textarea);
	}
}
